import { collection, addDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Log a proposal generation event with timing and metadata
 */
export const logGenerationMetric = async (metricData) => {
  try {
    const metricsRef = collection(db, 'metrics');
    await addDoc(metricsRef, {
      ...metricData,
      timestamp: new Date().toISOString(),
      createdAt: new Date()
    });
  } catch (error) {
    console.error('Error logging metric:', error);
    // Don't throw - we don't want to break the user's flow if metrics fail
  }
};

/**
 * Get all generation metrics
 */
export const getGenerationMetrics = async () => {
  try {
    const metricsRef = collection(db, 'metrics');
    const q = query(metricsRef, orderBy('createdAt', 'desc'), limit(1000));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching metrics:', error);
    throw error;
  }
};

/**
 * Get user-specific average generation time
 */
export const getUserAverageGenerationTime = async (userId) => {
  try {
    const metricsRef = collection(db, 'metrics');
    // Simplified query to avoid needing composite index
    const q = query(
      metricsRef, 
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    // Filter and sort in memory
    const metrics = snapshot.docs
      .map(doc => doc.data())
      .filter(m => m.status === 'success' && m.createdAt)
      .sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      })
      .slice(0, 10); // Last 10 successful generations
    
    if (metrics.length === 0) {
      return 15000; // Default 15 seconds if no history
    }
    
    const times = metrics
      .filter(m => m.elapsedTime && m.elapsedTime > 0)
      .map(m => m.elapsedTime);
    
    if (times.length === 0) {
      return 15000;
    }
    
    const average = times.reduce((a, b) => a + b, 0) / times.length;
    console.log(`📊 User average time: ${Math.round(average)}ms from ${times.length} generations`);
    return Math.round(average);
  } catch (error) {
    console.error('Error fetching user average time:', error);
    return 15000; // Default fallback
  }
};

/**
 * Calculate statistics from metrics
 */
export const calculateMetricsStats = (metrics) => {
  if (!metrics || metrics.length === 0) {
    return {
      totalGenerations: 0,
      averageTime: 0,
      medianTime: 0,
      minTime: 0,
      maxTime: 0,
      last24Hours: 0,
      last7Days: 0
    };
  }

  const times = metrics
    .filter(m => m.elapsedTime && m.elapsedTime > 0)
    .map(m => m.elapsedTime);

  if (times.length === 0) {
    return {
      totalGenerations: metrics.length,
      averageTime: 0,
      medianTime: 0,
      minTime: 0,
      maxTime: 0,
      last24Hours: 0,
      last7Days: 0
    };
  }

  // Calculate average
  const averageTime = times.reduce((a, b) => a + b, 0) / times.length;

  // Calculate median
  const sortedTimes = [...times].sort((a, b) => a - b);
  const medianTime = sortedTimes[Math.floor(sortedTimes.length / 2)];

  // Min and max
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);

  // Count recent generations
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const last24Hours = metrics.filter(m => {
    const metricDate = m.createdAt?.toDate ? m.createdAt.toDate() : new Date(m.timestamp);
    return metricDate >= oneDayAgo;
  }).length;

  const last7Days = metrics.filter(m => {
    const metricDate = m.createdAt?.toDate ? m.createdAt.toDate() : new Date(m.timestamp);
    return metricDate >= sevenDaysAgo;
  }).length;

  return {
    totalGenerations: metrics.length,
    averageTime: Math.round(averageTime),
    medianTime: Math.round(medianTime),
    minTime: Math.round(minTime),
    maxTime: Math.round(maxTime),
    last24Hours,
    last7Days
  };
};

/**
 * Format milliseconds to readable time
 */
export const formatTime = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = (ms / 1000).toFixed(1);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

